<script lang="ts">
  import { Control, FormControl, setupControl } from "../../forms/controls";
  import FormField from "../../forms/FormField.svelte";
  import InputGroup from "../../forms/InputGroup.svelte";
  import { Validator, type ValidatorMessages } from "../../forms/validators";
  import type { ControlValidator } from "../../forms/validators";
  import Overlay from "../../overlay/Overlay.svelte";

  export let label: string;
  export let selectedFn: (value: any) => string[];
  export let overlay: "inline" | "modal" = "inline";

  // either set name and validators
  export let name: string = null;
  export let required: boolean = false;
  export let validators: ControlValidator[] = [];
  export let value: any = undefined;
  // ... or bind a control
  export let control: FormControl = null;
  export let standalone: boolean = false;
  export let displayName: string = undefined;
  export let errors: ValidatorMessages = undefined;

  const changes = setupControl(FormControl, {
    name,
    validators: [...validators, ...(required ? [Validator.required] : [])],
    value,
    control,
    standalone,
  });
  $: ctrl = $changes;
  $: selected = selectedFn?.call(null, ctrl.value) || [];

  let oldValue = value;
  $: if (value !== oldValue) {
    oldValue = value;
    ctrl.value = value;
  }

  let openPanel = false;
  function handleClick() {
    openPanel = true;
  }
  function handleKeydown(e: KeyboardEvent) {
    if (e.code == "Space" || e.code == "Enter") handleClick();
  }

  function handleSubmit(overlay) {
    overlay.close();
  }

  let input: HTMLDivElement;
</script>

<FormField control={ctrl} displayName={displayName || label} {errors} {standalone} on:change>
  <div class="input-control input-dropdown" bind:this={input} class:input-error={ctrl.invalid} class:input-valid={ctrl.valid} class:input-dirty={ctrl.dirty} on:click={handleClick} on:keydown={(e) => handleKeydown(e)} tabindex="0">
    <div class="label">
      <slot name="label" control={ctrl}>
        {label}{#if required}&nbsp;*{/if}
      </slot>
    </div>
    <slot name="selected" control={ctrl}>
      {#if selected.length}
        <div class="selected">
          <span class="selected-count">{selected.length}</span>
          <span class="selected-label"
            >{selected.slice(0, 10).join(", ")}{#if selected.length > 3}, …{/if}</span
          >
        </div>
      {/if}
    </slot>
  </div>
</FormField>

<Overlay
  {overlay}
  {input}
  bind:open={openPanel}
  on:close={(_) => {
    ctrl.touch();
    ctrl.validate(true);
  }}
  on:close
  let:overlay
>
  <slot slot="title" name="title" />
  <InputGroup control={ctrl} standalone on:submit={(_) => handleSubmit(overlay)}>
    <slot control={ctrl} {overlay} />
  </InputGroup>
  <slot slot="footer" name="footer" />
</Overlay>

<style lang="scss" global>
  .input-dropdown {
    position: relative;
    display: flex;
    flex-flow: column;
    justify-content: center;
    background-color: #f7f7f7;
    padding: 0.375rem 1.55rem;
    min-height: 3.2rem;
    cursor: pointer;

    &:after {
      content: "";
      background: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='21.4' height='12.1' viewBox='0 0 21.4 12.1'%3E%3Cpath fill='none' stroke='%239f9f9f' stroke-width='2' d='m20.7.7-10 10L.7.7'/%3E%3C/svg%3E") no-repeat center/contain;
      position: absolute;
      top: 1em;
      right: 1em;
      bottom: 1em;
      width: 1.2em;
    }

    &:hover {
      background-color: darken(#f7f7f7, 2%);
    }
    &.input-error:hover {
      background-color: #ffd1d5;
    }

    .label {
      font-size: 18px;
    }

    .selected {
      display: flex;
      align-items: center;
      font-size: 12px;
      color: rgba(#000, 0.8);
      opacity: 0.65;
    }

    .selected-count {
      display: flex;
      align-items: center;
      justify-content: center;
      width: 1.5em;
      height: 1.5em;
      background-color: #000;
      color: #fff;
      opacity: 0.7;
      border-radius: 2px;
      padding: 0.2em;
      margin-right: 0.5em;
    }

    .selected-label {
      position: relative;
      white-space: nowrap;
      overflow: hidden;
      text-overflow: ellipsis;
      padding-right: 2rem;
    }
  }
</style>
